import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import HomePage from './pages/HomePage';
import SessionPage from './pages/SessionPage';
import SessionsListPage from './pages/SessionsListPage';
import QueueItemPage from './pages/QueueItemPage';
import PostingQueuePage from './pages/PostingQueuePage';
import LoginPage from './pages/LoginPage';
import CreditPurchasePage from './pages/CreditPurchasePage';
import PurchaseSuccessPage from './pages/PurchaseSuccessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import SessionSidebar from './components/SessionSidebar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserDataProvider } from './contexts/UserDataContext';
import api from './services/api';

const Navigation: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, signOut, deleteAccount, loading } = useAuth();
    const isHome = location.pathname === '/';
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    // one time mount initialization
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSignOut = async () => {
        try {
            await signOut();
            setUserMenuOpen(false);
            navigate('/');
        } catch (err) {
            console.error('Sign out error:', err);
        }
    };

    const handleDeleteAccount = async () => {
        setUserMenuOpen(false);

        // First confirmation
        if (!window.confirm(
            'Are you sure you want to delete your account?\n\n' +
            'This will permanently delete:\n' +
            '- Your profile and credits\n' +
            '- All Instagram accounts you own\n' +
            '- All sessions and comments\n\n' +
            'This action cannot be undone.'
        )) {
            return;
        }

        // Second confirmation - type DELETE
        const confirmation = window.prompt('Type DELETE to confirm account deletion:');
        if (confirmation?.toUpperCase() !== 'DELETE') {
            alert('Account deletion cancelled');
            return;
        }

        try {
            await deleteAccount();
            // Redirect to home page
            window.location.href = '/';
        } catch (err: any) {
            alert('Failed to delete account: ' + (err.message || 'Unknown error'));
        }
    };

    // Always use light mode for consistent look across all pages
    const navBackground = 'rgba(255, 255, 255, 0.8)';
    const navTextColor = '#000000';
    const navTextColorMuted = 'rgba(0, 0, 0, 0.7)';
    const showBorder = scrolled || !isHome;

    return (
        <>
            <nav style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                padding: '0 48px',
                height: '72px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: navBackground,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderBottom: showBorder ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                transition: 'all 0.3s ease',
            }}>
                {/* Logo */}
                <Link to="/" style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}>
                    <img
                        src="/logo-white-192.png"
                        alt="Vibe Commenting"
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        }}
                    />
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: navTextColor,
                        letterSpacing: '-0.02em',
                        transition: 'color 0.3s ease',
                    }}>
                        vibecommenting
                    </span>
                </Link>

                {/* Desktop Nav Links - only show for logged-out users */}
                {!user && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '40px',
                    }} className="desktop-nav">
                        <a href="/#features" style={{
                            color: navTextColorMuted,
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'color 0.3s ease',
                        }}>
                            Features
                        </a>
                        <a href="/#how-it-works" style={{
                            color: navTextColorMuted,
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'color 0.3s ease',
                        }}>
                            How it Works
                        </a>
                        <a href="/#pricing" style={{
                            color: navTextColorMuted,
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'color 0.3s ease',
                        }}>
                            Pricing
                        </a>
                    </div>
                )}

                {/* CTA Buttons */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                }} className="desktop-nav">
                    {loading ? (
                        <div style={{ width: '20px', height: '20px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: navTextColor, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    ) : user ? (
                        <>
                        <button
                            onClick={() => navigate('/plans')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: 'rgba(124, 58, 237, 0.08)',
                                border: '1px solid rgba(124, 58, 237, 0.2)',
                                borderRadius: '20px',
                                padding: '6px 14px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#7C3AED',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124, 58, 237, 0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(124, 58, 237, 0.08)'}
                            title="Credit balance - Click to buy more"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill="#F97316" />
                                <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight="bold" fill="white">C</text>
                            </svg>
                            {user.creditBalance ?? 0}
                        </button>
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    transition: 'background 0.2s ease',
                                }}
                            >
                                {user.photoUrl && (
                                    <img
                                        src={user.photoUrl}
                                        alt={user.displayName}
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                        }}
                                    />
                                )}
                                <span style={{
                                    color: navTextColorMuted,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    transition: 'color 0.3s ease',
                                }}>
                                    {user.displayName}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    style={{
                                        transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                    }}
                                >
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>

                            {userMenuOpen && (
                                <>
                                    {/* Backdrop to close menu */}
                                    <div
                                        style={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 999,
                                        }}
                                        onClick={() => setUserMenuOpen(false)}
                                    />
                                    {/* Dropdown menu */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '8px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        overflow: 'hidden',
                                        minWidth: '160px',
                                        zIndex: 1000,
                                    }}>
                                        <button
                                            onClick={handleSignOut}
                                            style={{
                                                width: '100%',
                                                background: 'transparent',
                                                border: 'none',
                                                padding: '12px 16px',
                                                textAlign: 'left',
                                                fontSize: '14px',
                                                color: '#333',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            Sign Out
                                        </button>
                                        <button
                                            onClick={handleDeleteAccount}
                                            style={{
                                                width: '100%',
                                                background: 'transparent',
                                                border: 'none',
                                                padding: '12px 16px',
                                                textAlign: 'left',
                                                fontSize: '14px',
                                                color: '#EF4444',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            Delete Account
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        </>
                    ) : (
                        <>
                            <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} style={{
                                background: 'transparent',
                                border: 'none',
                                color: navTextColorMuted,
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                padding: '8px 16px',
                                textDecoration: 'none',
                                transition: 'color 0.3s ease',
                            }}>
                                Log In
                            </Link>
                            <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} style={{
                                background: '#7C3AED',
                                color: '#FFFFFF',
                                padding: '12px 24px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 600,
                                textDecoration: 'none',
                                transition: 'all 0.2s ease',
                            }}>
                                Get Started
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{
                        display: 'none',
                        background: 'transparent',
                        border: 'none',
                        color: navTextColor,
                        cursor: 'pointer',
                        padding: '8px',
                        transition: 'color 0.3s ease',
                    }}
                    className="mobile-menu-btn"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {mobileMenuOpen ? (
                            <path d="M18 6L6 18M6 6l12 12" />
                        ) : (
                            <>
                                <line x1="3" y1="12" x2="21" y2="12" />
                                <line x1="3" y1="6" x2="21" y2="6" />
                                <line x1="3" y1="18" x2="21" y2="18" />
                            </>
                        )}
                    </svg>
                </button>
            </nav>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div style={{
                    position: 'fixed',
                    top: '72px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(10px)',
                    zIndex: 999,
                    padding: '32px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }} className="mobile-menu">
                    {!user && (
                        <>
                            <a href="/#features" onClick={() => setMobileMenuOpen(false)} style={{
                                color: '#000000',
                                textDecoration: 'none',
                                fontSize: '24px',
                                fontWeight: 500,
                            }}>
                                Features
                            </a>
                            <a href="/#how-it-works" onClick={() => setMobileMenuOpen(false)} style={{
                                color: '#000000',
                                textDecoration: 'none',
                                fontSize: '24px',
                                fontWeight: 500,
                            }}>
                                How it Works
                            </a>
                            <a href="/#pricing" onClick={() => setMobileMenuOpen(false)} style={{
                                color: '#000000',
                                textDecoration: 'none',
                                fontSize: '24px',
                                fontWeight: 500,
                            }}>
                                Pricing
                            </a>
                        </>
                    )}
                    <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {user ? (
                            <>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '16px 0',
                                }}>
                                    {user.photoUrl && (
                                        <img
                                            src={user.photoUrl}
                                            alt={user.displayName}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    )}
                                    <span style={{ color: '#000000', fontSize: '16px' }}>
                                        {user.displayName}
                                    </span>
                                </div>
                                <button
                                    onClick={() => { navigate('/plans'); setMobileMenuOpen(false); }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        background: 'rgba(124, 58, 237, 0.08)',
                                        border: '1px solid rgba(124, 58, 237, 0.2)',
                                        borderRadius: '8px',
                                        padding: '16px 24px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#7C3AED',
                                    }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="#F97316" />
                                        <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight="bold" fill="white">C</text>
                                    </svg>
                                    {user.creditBalance ?? 0} Credits
                                </button>
                                <button
                                    onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(0, 0, 0, 0.2)',
                                        color: '#000000',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        padding: '16px 24px',
                                        borderRadius: '4px',
                                    }}
                                >
                                    Sign Out
                                </button>
                                <button
                                    onClick={() => { setMobileMenuOpen(false); handleDeleteAccount(); }}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #EF4444',
                                        color: '#EF4444',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        padding: '16px 24px',
                                        borderRadius: '4px',
                                    }}
                                >
                                    Delete Account
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} onClick={() => setMobileMenuOpen(false)} style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    color: '#000000',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    padding: '16px 24px',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                }}>
                                    Log In
                                </Link>
                                <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} onClick={() => setMobileMenuOpen(false)} style={{
                                    background: '#7C3AED',
                                    color: '#FFFFFF',
                                    padding: '16px 24px',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                }}>
                                    Get Started
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            <style>
                {`
                    @media (max-width: 768px) {
                        .desktop-nav {
                            display: none !important;
                        }
                        .mobile-menu-btn {
                            display: block !important;
                        }
                    }
                `}
            </style>
        </>
    );
};

// Authenticated route - requires login
const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#000000',
            }}>
                <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid rgba(255, 255, 255, 0.1)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }} />
            </div>
        );
    }

    if (!user) {
        return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
    }

    return <>{children}</>;
};

// Session route - allows any user including anonymous (no login check)
// The backend enforces access control; the session page handles errors gracefully.
const SessionRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { loading } = useAuth();

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#000000',
            }}>
                <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid rgba(255, 255, 255, 0.1)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }} />
            </div>
        );
    }

    return <>{children}</>;
};

// Home route that redirects logged-in users to sessions list
const HomeRoute: React.FC = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#000000',
            }}>
                <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid rgba(255, 255, 255, 0.1)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }} />
            </div>
        );
    }

    // If user is logged in, always redirect to sessions (not the marketing homepage)
    if (user) {
        return <Navigate to="/sessions" replace />;
    }

    // Otherwise show the landing page
    return <HomePage />;
};

const AppContent: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const { getIdToken } = useAuth();

    // Set up auth provider for API client
    useEffect(() => {
        api.setAuthProvider(getIdToken);
    }, [getIdToken]);

    // Check if we're on a session, sessions list, or queue page (show sidebar for all)
    const isSessionsListPage = location.pathname === '/sessions';
    const isSessionPage = location.pathname.startsWith('/session/');
    const isQueuePage = location.pathname.startsWith('/queue/');
    const showSidebar = isSessionsListPage || isSessionPage || isQueuePage;

    // Extract current session ID or account ID from URL
    const currentSessionId = isSessionPage
        ? location.pathname.split('/session/')[1]
        : null;
    const currentAccountId = isQueuePage
        ? location.pathname.split('/queue/')[1]?.split('/')[0]
        : null;

    // Auto-open sidebar on session/queue pages, close on home
    useEffect(() => {
        if (showSidebar) {
            setSidebarOpen(true);
        } else {
            setSidebarOpen(false);
        }
    }, [showSidebar]);

    return (
        <>
            {/* Only render sidebar on session and queue pages */}
            {showSidebar && (
                <SessionSidebar
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    currentSessionId={currentSessionId}
                    currentAccountId={currentAccountId}
                />
            )}
            <main
                style={{
                    marginLeft: showSidebar && sidebarOpen ? '320px' : '0',
                    transition: 'margin-left 0.3s ease',
                }}
            >
                <Routes>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/plans" element={
                        <AuthenticatedRoute>
                            <CreditPurchasePage />
                        </AuthenticatedRoute>
                    } />
                    <Route path="/purchase/success" element={<PurchaseSuccessPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/sessions" element={
                        <AuthenticatedRoute>
                            <SessionsListPage />
                        </AuthenticatedRoute>
                    } />
                    <Route path="/session/:sessionId" element={
                        <SessionRoute>
                            <SessionPage />
                        </SessionRoute>
                    } />
                    <Route path="/queue/:accountId" element={
                        <AuthenticatedRoute>
                            <PostingQueuePage />
                        </AuthenticatedRoute>
                    } />
                    <Route path="/queue/:accountId/:queueItemId" element={
                        <AuthenticatedRoute>
                            <QueueItemPage />
                        </AuthenticatedRoute>
                    } />
                </Routes>
            </main>
        </>
    );
};

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <UserDataProvider>
                    <Router>
                        <Navigation />
                        <AppContent />
                    </Router>
                </UserDataProvider>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
